import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, ChevronRight, Clock, HelpCircle, MessageSquare, ShieldAlert, TrendingDown, X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { sendStudentMessage } from '../../api/teacher';
import { apiClient } from '../../api/client';
import { errorText } from '../../utils/errorText';
import { failure, failureText } from './apiError';
import { formatRelative } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from './pluralKey';
import Avatar from '../../components/Avatar/Avatar';
import Modal from '../../components/Modal/Modal';
import PageHead from '../../components/PageHead/PageHead';
import { usePolling } from '../../hooks/usePolling';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import './AtRiskDashboard.css';

// TODO: Replace with proper cursor-based or keyset pagination once the API supports it.

/**
 * Story 9.4-b: At-Risk Dashboard — darsly-crm Integration
 *
 * Pulls from GET /notifications, filtering for at-risk notification types:
 *   STUDENT_INACTIVE, LOW_QUIZ_SCORE, UNANSWERED_QUESTION
 *
 * Groups by risk type. Allows marking as read (removes from panel).
 * "Send Message" CTA pre-fills the message modal.
 */

const AT_RISK_TYPES = ['STUDENT_INACTIVE', 'LOW_QUIZ_SCORE', 'UNANSWERED_QUESTION'];

const RISK_TYPE_LABELS = {
  STUDENT_INACTIVE: { labelKey: 'teacher.atRisk.inactive', tone: 'warning', Icon: Clock },
  LOW_QUIZ_SCORE: { labelKey: 'teacher.atRisk.lowQuizScore', tone: 'danger', Icon: TrendingDown },
  UNANSWERED_QUESTION: { labelKey: 'teacher.atRisk.unansweredQuestion', tone: 'info', Icon: HelpCircle },
};

const FETCH_PAGE_LIMIT = 100; // Backend caps `limit` at 100 (ValidationPipe rejects larger values)

async function fetchAllAtRiskNotifications() {
  // Fetch all pages until a page returns fewer items than the limit.
  // Using a large page size (500) to minimise round-trips while still being safe
  // in case the server enforces a lower cap than the hard-coded 100 used previously.
  const allItems = [];
  let page = 1;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data } = await apiClient.get('/notifications', {
      params: { page, limit: FETCH_PAGE_LIMIT },
    });
    const items = Array.isArray(data) ? data : data.data || [];
    allItems.push(...items);
    if (items.length < FETCH_PAGE_LIMIT) {
      break;
    }
    page += 1;
  }

  return allItems;
}

async function markNotificationRead(notificationId) {
  const { data } = await apiClient.patch(`/notifications/${notificationId}/read`);
  return data;
}

/**
 * Parse student info from the notification.
 * GET /notifications puts who and what a teacher alert is about under `data`:
 * studentId, studentName, courseId, courseName (and threadId, lessonId for a Q&A alert).
 */
function parseStudentFromNotification(notification, t) {
  const meta = notification.metadata || notification.data || {};
  return {
    notificationId: notification.id,
    studentId: meta.studentId || null,
    studentName: meta.studentName || notification.title || t('teacher.atRisk.unknownStudent'),
    courseId: meta.courseId || null,
    courseName: meta.courseName || null,
    type: notification.type,
    isRead: notification.isRead || notification.read || false,
    createdAt: notification.createdAt,
    body: notification.body || notification.message || '',
  };
}

function AtRiskDashboard() {
  const navigate = useNavigate();
  // authLoaded indicates the auth context has finished its initial resolution.
  // It may be called `loaded`, `initialized`, `ready`, or similar — adjust to match
  // the actual AuthContext shape in this codebase.
  const { user, isLoading: authIsLoading } = useAuth();
  const { t, lang } = useI18n();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  // Track in-flight dismiss requests per notification ID to prevent double-dismiss.
  const [dismissingIds, setDismissingIds] = useState(new Set());
  const consecutiveFailuresRef = useRef(0);
  // The skeleton belongs to the first load only: a poll that finds nothing new must not flash it
  // over the "all on track" screen every 30 seconds.
  const hasLoadedRef = useRef(false);

  // Message modal state
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    studentId: null,
    studentName: null,
    courseId: null,
    prefillReason: '',
  });
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [msgError, setMsgError] = useState(null);

  const loadNotifications = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const all = await fetchAllAtRiskNotifications();
      // Filter to at-risk types only, unread
      const atRisk = all.filter(
        (n) => AT_RISK_TYPES.includes(n.type) && !n.isRead && !n.read
      );
      setNotifications(atRisk);
      setError(null);
      consecutiveFailuresRef.current = 0;
    } catch (err) {
      consecutiveFailuresRef.current += 1;
      setError(failure(err, t('teacher.atRisk.failedToLoad')));
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [t]);

  // Load on mount, then refresh every 30 seconds while the tab is visible (hooks/usePolling).
  // After 3 consecutive failures, polling is paused to avoid hammering the server.
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  usePolling(() => {
    // Back off — stop polling after repeated failures
    if (consecutiveFailuresRef.current >= 3) return;
    loadNotifications();
  }, 30000);

  const handleMarkRead = async (notificationId) => {
    // Guard against double-dismiss: if a PATCH is already in-flight for this ID, bail out.
    if (dismissingIds.has(notificationId)) return;

    setDismissingIds((prev) => new Set(prev).add(notificationId));
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (err) {
      setError(failure(err, t('teacher.atRisk.failedToMarkRead')));
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };

  const handleOpenMessageModal = (student) => {
    const reason = student.type === 'STUDENT_INACTIVE'
      ? t('teacher.atRisk.prefillInactive')
      : student.type === 'LOW_QUIZ_SCORE'
      ? t('teacher.atRisk.prefillLowQuizScore')
      : t('teacher.atRisk.prefillUnansweredQuestion');
    const prefill = t('teacher.atRisk.prefillMessage', { name: student.studentName, reason });

    setMessageModal({
      isOpen: true,
      studentId: student.studentId,
      studentName: student.studentName,
      courseId: student.courseId,
      prefillReason: prefill,
    });
    setMessageText(prefill);
    setMsgError(null);
  };

  const handleCloseMessageModal = () => {
    setMessageModal({ isOpen: false, studentId: null, studentName: null, courseId: null, prefillReason: '' });
    setMessageText('');
    setMsgError(null);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      setMsgError(t('teacher.atRisk.messageEmpty'));
      return;
    }
    if (!messageModal.studentId) {
      setMsgError(t('teacher.atRisk.missingStudentId'));
      return;
    }
    if (!messageModal.courseId) {
      // A message is sent in the context of a course the teacher is assigned to the student in.
      setMsgError(t('teacher.atRisk.missingCourseId'));
      return;
    }
    try {
      setSendingMessage(true);
      await sendStudentMessage(messageModal.studentId, {
        message: messageText,
        courseId: messageModal.courseId,
        type: 'intervention',
      });
      handleCloseMessageModal();
    } catch (err) {
      setMsgError(errorText(err, t('teacher.atRisk.failedToSendMessage')));
    } finally {
      setSendingMessage(false);
    }
  };

  // Group notifications by type
  const grouped = AT_RISK_TYPES.reduce((acc, type) => {
    acc[type] = notifications
      .filter((n) => n.type === type)
      .map((n) => parseStudentFromNotification(n, t));
    return acc;
  }, {});

  const totalAtRisk = notifications.length;
  const filledTypes = AT_RISK_TYPES.filter((type) => grouped[type].length > 0);
  // A reason that has been dismissed down to nothing falls back to "all".
  const activeFilter = filter !== 'all' && grouped[filter]?.length > 0 ? filter : 'all';
  const visibleTypes = activeFilter === 'all' ? filledTypes : [activeFilter];

  // While auth is still resolving, render nothing to prevent a flash of "Access Denied".
  if (authIsLoading && user === null) {
    return null;
  }

  // Simplified role guard — component is already wrapped in TeacherProtectedRoute;
  // this is a last-resort UI safeguard for unexpected role mismatches.
  if (user?.role !== 'TEACHER') {
    return (
      <div className="at-risk-dashboard">
        <PageHead icon={<ShieldAlert />} title={t('teacher.atRisk.title')} />
        <PageEmptyState
          variant="forbidden"
          title={t('teacher.atRisk.accessDenied')}
          description={t('teacher.atRisk.accessDeniedMsg')}
          actionLabel={t('teacher.atRisk.returnToDashboard')}
          onAction={() => navigate('/teacher/dashboard')}
        />
      </div>
    );
  }

  if (loading && notifications.length === 0) {
    return (
      <div className="at-risk-dashboard">
        <PageHead icon={<ShieldAlert />} title={t('teacher.atRisk.title')} />
        {/* Bones in the shape of an alert card (a person, two lines, two buttons), not a course card */}
        <div className="card-grid ar-grid" role="status" aria-busy="true" aria-label={t('teacher.atRisk.loading')}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="section-card ar-card" aria-hidden="true">
              <div className="ar-card__head">
                <div className="ui-skel ar-skel-avatar" />
                <div className="ar-card__who">
                  <div className="ui-skel ar-skel-line" />
                  <div className="ui-skel ar-skel-line ar-skel-line--short" />
                </div>
              </div>
              <div className="ui-skel ar-skel-line" />
              <div className="ui-skel ar-skel-line ar-skel-line--mid" />
              <div className="ar-card__actions">
                <div className="ui-skel ar-skel-pill" />
                <div className="ui-skel ar-skel-pill ar-skel-pill--narrow" />
              </div>
            </div>
          ))}
          <span className="visually-hidden">{t('teacher.atRisk.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="at-risk-dashboard">
      <PageHead
        icon={<ShieldAlert />}
        title={t('teacher.atRisk.title')}
        description={totalAtRisk > 0
          ? t(`teacher.atRisk.subtitleNeedAttention${pluralSuffix(lang, totalAtRisk)}`, { count: totalAtRisk })
          : t('teacher.atRisk.subtitleAllOnTrack')}
      />

      {error && totalAtRisk > 0 && (
        <div className="ui-alert ar-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{failureText(error)}</p>
        </div>
      )}

      {totalAtRisk === 0 ? (
        error ? (
          <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={loadNotifications} />
        ) : (
          <PageEmptyState
            variant="success"
            title={t('teacher.atRisk.allOnTrack')}
            description={t('teacher.atRisk.noActiveNotifications')}
          />
        )
      ) : (
        <div className="students-container">
          {filledTypes.length > 1 && (
            <div className="ui-chip-row ar-filters" role="group" aria-label={t('teacher.atRisk.filterAria')}>
              <button
                type="button"
                className={`ui-chip${activeFilter === 'all' ? ' is-active' : ''}`}
                aria-pressed={activeFilter === 'all'}
                onClick={() => setFilter('all')}
              >
                {t('teacher.atRisk.filterAll')}
                <span className="ui-count">{totalAtRisk}</span>
              </button>
              {filledTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`ui-chip${activeFilter === type ? ' is-active' : ''}`}
                  aria-pressed={activeFilter === type}
                  onClick={() => setFilter(type)}
                >
                  {t(RISK_TYPE_LABELS[type].labelKey)}
                  <span className="ui-count">{grouped[type].length}</span>
                </button>
              ))}
            </div>
          )}

          {visibleTypes.map((type) => {
            const group = grouped[type];
            const meta = RISK_TYPE_LABELS[type];
            const metaLabel = t(meta.labelKey);
            const { Icon } = meta;
            return (
              <section key={type} className="ar-group" aria-label={metaLabel}>
                <div className="section-head">
                  <div className="ar-group__title">
                    <span className="ui-icon-chip ui-icon-chip--sm" data-tone={meta.tone} aria-hidden="true"><Icon /></span>
                    <h2 className="section-head__title">{metaLabel}</h2>
                    <span className="ui-count">{group.length}</span>
                  </div>
                </div>
                <div className="card-grid ar-grid">
                  {group.map((student) => (
                    <article
                      key={student.notificationId}
                      className="section-card ar-card student-card"
                    >
                      <div className="ar-card__head">
                        <Avatar name={student.studentName} id={student.studentId || student.notificationId} />
                        <div className="ar-card__who">
                          <h3 className="ar-card__name">{student.studentName}</h3>
                          {student.courseName && (
                            <p className="student-course">{student.courseName}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="ui-icon-btn ar-card__dismiss"
                          onClick={() => handleMarkRead(student.notificationId)}
                          disabled={dismissingIds.has(student.notificationId)}
                          aria-label={dismissingIds.has(student.notificationId) ? t('teacher.atRisk.dismissing') : t('teacher.atRisk.dismiss')}
                          title={t('teacher.atRisk.dismiss')}
                        >
                          <X aria-hidden="true" />
                        </button>
                      </div>

                      {student.body && (
                        <p className="notification-body">{student.body}</p>
                      )}
                      {student.createdAt && (
                        <p className="ar-card__time">{formatRelative(student.createdAt, { lang })}</p>
                      )}

                      <div className="action-buttons ar-card__actions">
                        <button
                          type="button"
                          className="ds-btn ds-btn-secondary ds-btn-sm"
                          onClick={() => handleOpenMessageModal(student)}
                          disabled={!student.studentId || !student.courseId}
                          title={
                            !student.studentId
                              ? t('teacher.atRisk.studentIdUnavailable')
                              : !student.courseId
                                ? t('teacher.atRisk.courseUnavailable')
                                : ''
                          }
                        >
                          <MessageSquare aria-hidden="true" />
                          {t('teacher.atRisk.sendMessage')}
                        </button>
                        {student.studentId && student.courseId && (
                          <button
                            type="button"
                            className="ds-btn ds-btn-ghost ds-btn-sm"
                            onClick={() => navigate({
                              pathname: `/teacher/students/${student.studentId}`,
                              // courseId also travels in the query string (not just
                              // location.state) so the page survives a refresh/bookmark.
                              search: `?courseId=${encodeURIComponent(student.courseId)}`
                            }, {
                              state: { courseId: student.courseId }
                            })}
                          >
                            {t('teacher.atRisk.viewDetails')}
                            <ChevronRight aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Message dialog */}
      <Modal
        open={messageModal.isOpen}
        onClose={sendingMessage ? undefined : handleCloseMessageModal}
        title={t('teacher.atRisk.sendMessageTo', { name: messageModal.studentName })}
        size="md"
        footer={(
          <>
            <button
              type="button"
              className="ds-btn ds-btn-secondary"
              onClick={handleCloseMessageModal}
              disabled={sendingMessage}
            >
              {t('teacher.atRisk.cancel')}
            </button>
            <button
              type="button"
              className={`ds-btn ds-btn-primary${sendingMessage ? ' is-loading' : ''}`}
              onClick={handleSendMessage}
              disabled={sendingMessage || !messageText.trim()}
              aria-busy={sendingMessage || undefined}
            >
              {t('teacher.atRisk.sendMessage')}
            </button>
          </>
        )}
      >
        {msgError && (
          <div className="ui-alert ar-modal-alert" role="alert">
            <AlertCircle aria-hidden="true" />
            <p>{msgError}</p>
          </div>
        )}
        <textarea
          data-autofocus
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder={t('teacher.atRisk.messagePlaceholder')}
          aria-label={t('teacher.atRisk.messagePlaceholder')}
          rows="6"
          className="message-textarea ar-textarea"
        />
      </Modal>
    </div>
  );
}

export default AtRiskDashboard;
